import { Controller } from "@hotwired/stimulus"

// LRPO-5513 — the only date format a native <input type="date"> emits. Also the
// format the server parses (Date.parse in ReportsController#safe_date_parse) and
// the one the event-window data attributes are stamped in, so a plain string
// compare between any two of them is a chronological compare.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Reports Hub date-preset filter.
 *
 * Root element wraps the filter bar. The form inside submits as a GET
 * into the reports_tab_content Turbo frame so that only the active tab
 * reloads on a filter change.
 *
 * Targets (from _filter_bar.html.erb):
 *   - form:        the <form> submitted on preset change
 *   - presetGroup: the radio btn-group (unused in logic but available)
 *   - presetInput: each radio input (one per preset)
 *   - customRange: container for Custom-preset date inputs (show/hide)
 *   - dateInput:   the date_from / date_to inputs inside customRange
 *   - clear:       the "Clear filters" wrapper (show/hide)
 *
 * Values:
 *   - activePreset: current active preset (used for initial render)
 *
 * Behavior:
 *   - When user picks a non-custom preset → syncs custom visibility +
 *     submits the form so the tab reloads with the new date window.
 *   - When user picks "custom" → reveals date inputs and waits for the
 *     user to hit Apply (no auto-submit — `from`/`to` aren't set yet).
 *   - Clear-filters visibility is derived from the CURRENT CLIENT state on
 *     every relevant change (see below).
 *
 * --- LRPO-5512 — Clear-filters visibility is CLIENT state, not SSR state ---
 *
 * "Clear filters" used to be rendered CONDITIONALLY server-side
 * (`if @date_preset != 'full_event' || @date_from.present? || @date_to.present?`),
 * so its very PRESENCE in the DOM was a function of the request params of the
 * last FULL-PAGE load. But this filter bar lives in the `.reports-context-strip`
 * header, which sits OUTSIDE the `reports_tab_content` Turbo Frame: a filter
 * apply / preset change / tab click swaps only the inner frame and NEVER
 * re-renders this shell. Both halves of the QA repro follow from that:
 *
 *   (A) On a default (full_event) page the wrapper was absent from the DOM.
 *       Choosing "Custom" — and even applying a range — swapped only the frame,
 *       so Clear did not appear until the user hard-refreshed and the server
 *       re-rendered the shell with the custom params.
 *
 *   (B) On a page loaded WITH a custom range the wrapper was present. Clicking
 *       Clear navigated the frame back to the unfiltered path, but again nothing
 *       re-rendered the shell — so the button (and the filled date inputs)
 *       stayed on screen even though the filter was gone.
 *
 * Fix: the view ALWAYS renders the wrapper and marks it
 * `data-reports-filter-target="clear"`; its server-rendered `d-none` merely
 * seeds the correct FIRST PAINT (and the no-JS case). From then on
 * `_syncClearVisibility()` recomputes it from the LIVE control state — checked
 * preset + date-input values — on connect, on every preset change, on every
 * date-input change, and on the Clear click itself. No reload is involved.
 *
 * `clear()` additionally resets the controls (re-check full_event, blank both
 * date inputs, collapse the custom row) BEFORE letting the click through, so the
 * chrome is correct the instant the user clicks rather than only once the frame
 * response lands. It deliberately does NOT preventDefault: the link is still a
 * Turbo GET nav to the unfiltered path and that nav must proceed.
 *
 * reports_page_controller#_syncChrome performs the equivalent toggle from the
 * URL on `turbo:render` (back/forward, deep links, post-apply Drive render). The
 * two are intentionally parallel — same predicate, different triggers — mirroring
 * the customRange `d-none` split already documented there.
 *
 * --- LRPO-5513 — "Date range blocked during processing" -----------------------
 *
 * QA: pick an invalid or very large Custom range → Apply → the request grinds
 * for a long time and comes back with an error, and Apply stays DISABLED the
 * whole time, so the user is locked out of fixing the very range they typed.
 * Two defects compound:
 *
 *   1. NO CLIENT-SIDE VALIDATION. An obviously-invalid range (from > to, a
 *      half-filled range, bounds far outside the event) was dispatched to the
 *      server at all. A range far outside the event is also the expensive one:
 *      `ReportsController#date_group_opts` hands `@start_date..@end_date` to
 *      groupdate as the zero-fill `range:`, so a 1900→2100 window asks the
 *      server to materialise one bucket PER DAY across the whole span.
 *
 *   2. THE DISABLED-BUTTON TRAP. Turbo disables the submitter for the life of a
 *      submission (FormSubmission#requestStarted →
 *      `submitter.setAttribute("disabled", "")`, cleared in #requestFinished).
 *      That is a legitimate double-submit guard — but it had no escape hatch:
 *      the date inputs stay editable (Turbo does not disable them) while the one
 *      control that could act on the correction is dead.
 *
 * Fix, in three parts:
 *
 *   * submit() validates a Custom range BEFORE anything is dispatched. On a bad
 *     range it preventDefault()s (Turbo's FormSubmitObserver bails on
 *     `event.defaultPrevented`, so no request is made) and stopImmediate-
 *     Propagation()s so the `submit->engine-telemetry#emit` binding that sits
 *     AFTER ours on the same element never logs a `filter_applied` that did not
 *     happen. The reason is shown inline (role="alert") instead.
 *
 *   * The chrome is re-derived from ONE reconciler, _syncBusyChrome(), on every
 *     relevant event (submit / turbo:submit-end / date change / preset change /
 *     Clear). Apply is disabled only while a request is in flight AND the live
 *     range still equals the one in flight. The moment the user's edit DIVERGES
 *     the button comes back, because a divergent edit means they want to
 *     supersede the outstanding request. Re-asserting the state from a single
 *     predicate also survives Turbo's own `disabled` toggling: an aborted
 *     submission's requestFinished removes the attribute even though a newer
 *     request is still running, and the next reconcile puts it back.
 *
 *   * A new submission supersedes the outstanding one. Turbo already aborts it
 *     (FrameController#formSubmitted calls `this.formSubmission.stop()` on the
 *     previous submission to the same frame), and beforeFetchResponse() closes
 *     the race from the other end: any response whose URL does not carry the
 *     NEWEST submitted signature is cancelled on `turbo:before-fetch-response`
 *     (a cancelable event) so a slow, stale response can never overwrite a newer
 *     one.
 *
 * A large-but-VALID range is still allowed — it just must not look broken: an
 * accepted submit marks the bar `aria-busy` and reveals the status region, which
 * both clear when the submission settles.
 *
 * --- LRPO-5513 (QA RETURN) — the message must name the window it enforces -----
 *
 * QA came back: dates before the exhibitor's First Scan date are disabled, and
 * typing one is refused with "Choose dates within the event window (X to Y)" —
 * "even though the actual event date range is different".
 *
 * They are right, and the bug is the SENTENCE, not the floor. The window pinned
 * on the pickers (and validated here) is the exhibitor's REPORTING window:
 *
 *     from = effective_start_date = scan_start_date || event.start_date
 *     to   = effective_end_date   = max(event.end_date, today)
 *
 * Neither bound is the event's. With a scan_start_date set, the floor sits ABOVE
 * event.start_date — so the old message told the user to stay inside "the event
 * window" while printing dates that were not the event's, and never said why the
 * earlier dates had gone. The floor is deliberate (reporting is scoped to
 * effective_date_range) and is KEPT; _floorMessage / _ceilingMessage now state the
 * true window and the real reason, and `data-window-floor` (server-stamped — only
 * the server knows whether the floor came from a scan date or the event start)
 * picks which reason. The same explanation rides the tooltip next to the pickers.
 *
 * ⚠️ The messages say "REPORTING starts/ends at X" — never "no data before X".
 * Leads DO exist outside this window (pre-event scanning is the whole reason
 * scan_start_date exists, and it is a declared setting, not a derived fact); they
 * are simply out of scope for reporting. Claiming they do not exist contradicts
 * the Leads grid, which applies no date window at all.
 *
 * The other half of the QA note — "the system becomes unresponsive when a distant
 * or invalid First Scan date is selected" — is NOT fixed by the LRPO-5514 clamp,
 * and it was wrong to defer it there. That clamp pulls a custom date_from/date_to
 * INTO effective_start_date..effective_end_date, which is a no-op when the WINDOW
 * ITSELF is the poison: a First Scan date of 1900-01-01 used to save cleanly (no
 * lower-bound validation), making effective_start_date 1900-01-01, so the clamp
 * target IS the bad value and the repro never even enters the `custom` branch —
 * it is the DEFAULT full_event window that is ~126 years wide. It also silently
 * defeated the guard in this very file, since the pickers take `min` from that
 * same poisoned bound. Fixed at the source in EventExhibitor: a lower bound on
 * scan_start_date, plus a read-time floor in effective_start_date for rows that
 * are already poisoned. See event_exhibitor.rb and
 * spec/models/event_exhibitor_scan_start_floor_spec.rb.
 *
 * Unapplied edits are advertised on the root as `data-reports-filter-dirty`.
 * reports_page_controller#_syncChrome repopulates the date inputs from the URL on
 * every Drive render; without the flag, the response to the range the user just
 * ABANDONED would land and overwrite the correction they are still typing —
 * re-arming the trap this ticket exists to remove.
 */
export default class extends Controller {
  static targets = [
    "form", "presetGroup", "presetInput", "customRange", "dateInput", "clear",
    "apply", "error", "status"
  ]
  static values = {
    activePreset: { type: String, default: "full_event" }
  }

  connect() {
    // Number of submissions started but not yet settled. A superseded submission
    // still fires `turbo:submit-end` when Turbo aborts it, so this can only be
    // driven to 0 by as many ends as there were starts.
    this._pending = 0
    // The newest signature actually SUBMITTED. Seeded from the server-rendered
    // controls, which mirror the URL — on first paint the applied state and the
    // control state are the same thing.
    this._submittedSignature = this._signature()

    this._syncCustomVisibility()
    this._syncClearVisibility()
    this._syncBusyChrome()
  }

  presetChanged(event) {
    const preset = event?.target?.value || this._activePreset()
    this.activePresetValue = preset
    this._syncCustomVisibility()
    this._syncClearVisibility()
    // Leaving Custom retires any range complaint — the other presets derive
    // their window from the preset alone and never read these inputs.
    if (preset !== "custom") this._clearError()
    this._syncBusyChrome()

    if (preset !== "custom" && this.hasFormTarget) {
      // requestSubmit fires normal form submission (including Turbo's
      // frame interception via data-turbo-frame on the form).
      if (typeof this.formTarget.requestSubmit === "function") {
        this.formTarget.requestSubmit()
      } else {
        this.formTarget.submit()
      }
    }
  }

  // A date bound was picked / typed / blanked. Re-derive Clear visibility so it
  // tracks the live range even before the user hits Apply.
  dateChanged() {
    this._syncClearVisibility()

    // LRPO-5513 — validate as they go: a broken range says so the moment it is
    // broken, and stops saying so the moment it is fixed. No round-trip either
    // way. Never steals focus here (the user is still in the field).
    const problem = this._validate()
    if (problem) this._showError(problem)
    else this._clearError()

    // The escape hatch: an edit that diverges from the in-flight range re-enables
    // Apply so the user can supersede a slow request instead of waiting it out.
    this._syncBusyChrome()
  }

  // LRPO-5513 — the ONLY gate between the user and the server. Runs before the
  // engine-telemetry binding on the same `submit` event (action order in
  // `_filter_bar.html.erb`), so a rejected submit is neither dispatched nor
  // logged as an apply.
  submit(event) {
    const problem = this._validate()
    if (problem) {
      event.preventDefault()
      event.stopImmediatePropagation()
      this._showError(problem, { focus: true })
      return
    }
    this._clearError()

    const signature = this._signature()

    // The double-submit guard the disabled button exists for, enforced where it
    // actually counts. The disabled attribute is chrome — an unchanged range can
    // still reach here via implicit (Enter-key) submission or a stray
    // requestSubmit(), and re-running an identical query buys nothing.
    if (this._pending > 0 && signature === this._submittedSignature) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    this._pending += 1
    this._submittedSignature = signature
    this._syncBusyChrome()
  }

  // `turbo:submit-end` — the submission settled (rendered, failed, or was
  // aborted by a newer one). Only the LAST one to settle clears the busy chrome.
  submitEnd() {
    if (this._pending > 0) this._pending -= 1
    this._syncBusyChrome()
  }

  // LRPO-5513 — drop a superseded response before it can render.
  //
  // `turbo:before-fetch-response` is cancelable and fires on the form for every
  // response to a submission from this form. If its URL does not carry the
  // NEWEST submitted signature it belongs to a range the user has already
  // abandoned; letting it render would swap the frame back to stale content
  // after (or instead of) the range they actually want. Turbo aborts the
  // superseded request itself, so this is the belt to that braces — but it is
  // the half that holds when a response was already in flight at abort time.
  beforeFetchResponse(event) {
    const signature = this._signatureFromResponse(event)
    if (signature === null) return // unparseable → not ours to judge; let it render
    if (signature === this._submittedSignature) return

    event.preventDefault()
  }

  // LRPO-5512 — "Clear filters" clicked. Reset the controls to the default state
  // immediately, then let the Turbo GET nav to the unfiltered path proceed (the
  // frame reloads with no date params). No preventDefault.
  clear() {
    if (this.hasPresetInputTarget) {
      this.presetInputTargets.forEach(input => {
        input.checked = input.value === "full_event"
      })
    }
    this.activePresetValue = "full_event"

    if (this.hasDateInputTarget) {
      this.dateInputTargets.forEach(input => { input.value = "" })
    }

    // LRPO-5513 — Clear IS an apply of the default filter (the link is a Turbo
    // GET to the unfiltered path). Adopting the reset state as the newest
    // submitted signature retires any range complaint, leaves no phantom
    // unapplied edit behind, and makes an in-flight response for the range being
    // cleared stale — so it cannot land and repaint the filter the user just
    // dropped.
    this._submittedSignature = this._signature()
    this._clearError()

    this._syncCustomVisibility()
    this._syncClearVisibility()
    this._syncBusyChrome()
  }

  _syncCustomVisibility() {
    if (!this.hasCustomRangeTarget) return
    const showCustom = this._activePreset() === "custom"
    this.customRangeTarget.classList.toggle("d-none", !showCustom)
  }

  // Show "Clear filters" whenever the CURRENT client state is non-default — the
  // same predicate the server uses to seed the first paint.
  _syncClearVisibility() {
    if (!this.hasClearTarget) return
    this.clearTarget.classList.toggle("d-none", !this._filtersApplied())
  }

  // Non-default state = a preset other than full_event, OR either custom bound
  // filled. Mirrors `_filter_bar.html.erb`'s SSR predicate exactly.
  _filtersApplied() {
    if (this._activePreset() !== "full_event") return true

    return this.hasDateInputTarget &&
      this.dateInputTargets.some(input => (input.value || "").trim() !== "")
  }

  _activePreset() {
    if (this.hasPresetInputTarget) {
      const checked = this.presetInputTargets.find(r => r.checked)
      if (checked?.value) return checked.value
    }
    return this.activePresetValue
  }

  // ---------------------------------------------------------------------------
  // LRPO-5513 — validation
  // ---------------------------------------------------------------------------

  // Returns `{ message, input }` for an unusable Custom range, or null.
  //
  // ONLY the 'custom' preset is validated. The date inputs are always in the DOM
  // and always submit (they are merely `d-none`-toggled — LRPO-5145), so stale
  // values ride along on a today/yesterday/full_event submit; but those presets
  // derive their window from the preset alone (ReportsController#set_date_range),
  // so validating them here would block a perfectly good preset change.
  _validate() {
    if (this._activePreset() !== "custom") return null

    const fromInput = this._dateInput("date_from")
    const toInput = this._dateInput("date_to")
    if (!fromInput || !toInput) return null

    const from = (fromInput.value || "").trim()
    const to = (toInput.value || "").trim()

    if (!from || !to) {
      return {
        message: "Choose both a From and a To date.",
        input: from ? toInput : fromInput
      }
    }

    // A native date input can only hand back ISO-8601, but a text fallback (or a
    // hand-edited DOM) can hand back anything. Reject rather than guess.
    if (!ISO_DATE.test(from) || !ISO_DATE.test(to)) {
      return {
        message: "Enter each date as YYYY-MM-DD.",
        input: ISO_DATE.test(from) ? toInput : fromInput
      }
    }

    // ISO-8601 dates sort lexicographically, so a string compare IS a date
    // compare — and it cannot drift by a timezone the way Date parsing can.
    if (from > to) {
      return {
        message: "The From date must be on or before the To date.",
        input: fromInput
      }
    }

    // Bounds outside the exhibitor's REPORTING window (the same window the
    // full_event preset reports on) return nothing but cost everything: the
    // server zero-fills the per-day chart series across the whole span. The
    // inputs also carry min/max, so the picker will not offer these — this
    // catches a typed or pasted value.
    const reportWindow = this._reportWindow()
    if (reportWindow && (from < reportWindow.from || to > reportWindow.to)) {
      const belowFloor = from < reportWindow.from
      return {
        message: belowFloor ? this._floorMessage(reportWindow) : this._ceilingMessage(reportWindow),
        input: belowFloor ? fromInput : toInput
      }
    }

    return null
  }

  // LRPO-5513 (QA return) — the sentence the user actually reads.
  //
  // It used to say "Choose dates within the event window (X to Y)". X/Y were
  // right; "the event window" was NOT — X is `effective_start_date`
  // (`scan_start_date || event.start_date`), so for any exhibitor who has set a
  // First Scan date the enforced range starts AFTER the event does. QA typed a
  // real event date, was refused, and was pointed at an "event window" whose
  // printed dates were not the event's. The floor is deliberate — the message
  // just has to be TRUE about it, and has to say WHY the earlier dates are gone.
  //
  // ⚠️ "No data before X" was the SECOND false claim and is gone too. Reporting
  // being floored at X does NOT mean no leads exist before X — exhibitors
  // "routinely scan leads weeks before the official event start_date" (see
  // EventExhibitor#effective_start_date), and scan_start_date is a DECLARED
  // setting, not a derived fact. Those pre-floor leads exist and are visible on
  // the Leads grid; they are merely OUT OF SCOPE FOR REPORTING. Say that, and
  // nothing more. Same for the ceiling: effective_end_date is max(event.end_date,
  // today), and a future-dated scan can sit beyond it, so "No data after Y" was
  // false in the same way.
  //
  // `data-window-floor` (server-stamped: the client sees one date and cannot
  // tell a scan floor from an event-start floor) selects the reason, so we never
  // credit a first scan date the exhibitor never set.
  //
  // THIRD STATE — "clamped". EventExhibitor#effective_start_date floors a stored
  // first scan date that sits below `event.start_date - MAX_PRE_EVENT_SCAN_DAYS`,
  // so for such a row the bound we enforce is NOT the stored value. Both other
  // sentences would be false about it ("your first scan date (2025-07-01)" for a
  // row storing 1900-01-01 was the rejected revision's bug), and both would also
  // be UN-ACTIONABLE: they tell the user to move their First Scan date earlier,
  // which the model's lower-bound validation refuses below this very bound. So the
  // clamped copy names the real constraint and asks for the only thing that works
  // — correcting the out-of-bounds value. Mirrors the tooltip in
  // `_filter_bar.html.erb`; both are pinned (JS: reports_filter_window_message.test.js,
  // ERB: spec/requests/exhibitor/reports/date_range_window_hint_spec.rb).
  _floorMessage({ from, to }) {
    const floor = this._floorReason()

    if (floor === "clamped") {
      // Both are server-stamped alongside the "clamped" floor. If a STALE cached
      // shell carries the floor without them, we simply say less — never something
      // false. That is the whole reason this is not derived client-side.
      const declared = this.element.dataset.declaredScanStart
      const maxDays = this.element.dataset.maxPreEventDays

      const width = maxDays ? ` (${maxDays} days before it starts)` : ""
      const stored = declared
        ? ` Your first scan date (${declared}) is outside that range; correct it under Scan Dates.`
        : ""

      return `Reporting starts at ${from} — the earliest date this event supports${width}.` +
        `${stored} Choose dates between ${from} and ${to}.`
    }

    const reason = floor === "first_scan" ? "your first scan date" : "the event start date"

    return `Reporting starts at ${from} (${reason}). Choose dates between ${from} and ${to}.`
  }

  _ceilingMessage({ from, to }) {
    return `Reporting ends at ${to}. Choose dates between ${from} and ${to}.`
  }

  // "first_scan" | "clamped" | "event_start". Falls back to the event-start wording
  // for an absent or UNRECOGNISED value (e.g. an older cached shell, or a floor
  // state added server-side before this file ships): it is the weakest claim, and
  // asserting a first scan date that was never set — or one that is not the date
  // being enforced — would be a lie about the user's own settings.
  _floorReason() {
    const floor = this.element.dataset.windowFloor

    return floor === "first_scan" || floor === "clamped" ? floor : "event_start"
  }

  _showError(problem, { focus = false } = {}) {
    if (this.hasDateInputTarget) {
      this.dateInputTargets.forEach(input => input.removeAttribute("aria-invalid"))
    }

    if (problem.input) {
      problem.input.setAttribute("aria-invalid", "true")
      // Only on an explicit Apply — moving focus on every keystroke-ish `change`
      // would fight the user while they are still filling the other bound.
      if (focus && typeof problem.input.focus === "function") problem.input.focus()
    }

    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = problem.message
    this.errorTarget.classList.remove("d-none")
  }

  _clearError() {
    if (this.hasDateInputTarget) {
      this.dateInputTargets.forEach(input => input.removeAttribute("aria-invalid"))
    }
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("d-none")
  }

  // The exhibitor's effective REPORTING window, stamped on the root by
  // `_filter_bar.html.erb` as data-full-event-from / -to (shared with
  // lead_drill.js, which uses it to carry the full_event window into a
  // drilldown). NOT the event's date range: the lower bound is
  // `effective_start_date` (= scan_start_date || event.start_date) and the upper
  // is `effective_end_date` (= max(event.end_date, today)) — hence the rename,
  // and hence the messages above must never call it "the event window". Absent
  // for a draft event with no resolvable bounds — then there is no window to
  // validate against and the check is skipped.
  _reportWindow() {
    const from = this.element.dataset.fullEventFrom
    const to = this.element.dataset.fullEventTo
    if (!ISO_DATE.test(from || "") || !ISO_DATE.test(to || "")) return null
    if (from > to) return null

    return { from, to }
  }

  // ---------------------------------------------------------------------------
  // LRPO-5513 — in-flight state
  // ---------------------------------------------------------------------------

  // Single reconciler for everything that depends on "is a request in flight and
  // does the live range still match it". Called from every path that can change
  // either half, so no ordering between Turbo's own `disabled` toggling and ours
  // can leave the button stuck.
  _syncBusyChrome() {
    const busy = this._pending > 0
    const dirty = this._signature() !== this._submittedSignature

    // Read by reports_page_controller#_syncChrome, which must not repopulate the
    // date inputs from the URL while the user has edits it would destroy.
    this.element.dataset.reportsFilterDirty = dirty ? "true" : "false"

    if (busy) this.element.setAttribute("aria-busy", "true")
    else this.element.removeAttribute("aria-busy")

    // A slow but legitimate query must look like it is working.
    if (this.hasStatusTarget) this.statusTarget.classList.toggle("d-none", !busy)

    // Disabled ONLY while the in-flight range is still the one on screen. Once
    // the user edits it, the button is their way to supersede the request — the
    // whole point of the ticket — and it is never a double submit, because the
    // range being submitted is by definition not the one in flight.
    if (this.hasApplyTarget) this.applyTarget.disabled = busy && !dirty
  }

  // The identity of a filter state: preset + both bounds. Two submissions with
  // the same signature request the same thing.
  _signature() {
    return [
      this._activePreset(),
      this._dateValue("date_from"),
      this._dateValue("date_to")
    ].join("|")
  }

  // The same identity, read back off a response URL so it can be compared with
  // the newest submitted one. Returns null when there is nothing to compare.
  _signatureFromResponse(event) {
    const fetchResponse = event?.detail?.fetchResponse
    const href = fetchResponse?.response?.url || fetchResponse?.location?.href
    if (!href) return null

    let params
    try {
      params = new URL(href, window.location.href).searchParams
    } catch (_e) {
      return null
    }

    return [
      params.get("date_preset") || "full_event",
      params.get("date_from") || "",
      params.get("date_to") || ""
    ].join("|")
  }

  _dateInput(name) {
    if (!this.hasDateInputTarget) return null
    return this.dateInputTargets.find(input => input.name === name) || null
  }

  _dateValue(name) {
    const input = this._dateInput(name)
    return input ? (input.value || "").trim() : ""
  }
}
